import React, { useState } from 'react';
import {
    Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
    Chip, Divider, Alert
} from '@mui/material';
import {
    MenuBook, ExpandMore, CheckCircleOutline, Info,
    Assessment, Receipt, Settings, Calculate
} from '@mui/icons-material';
import { COLORS } from '../../settings/themeSettings';
import { useNavigate } from 'react-router-dom'; // eslint-disable-line no-unused-vars

// --- Composant d'une étape numérotée ---
const Step = ({ number, title, description }) => (
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{
            minWidth: 32, height: 32, borderRadius: '50%',
            backgroundColor: COLORS.secondary, color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 'bold', fontSize: '0.9rem', mt: 0.2
        }}>
            {number}
        </Box>
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: COLORS.secondary }}>{title}</Typography>
            <Typography variant="body2" sx={{ color: '#555' }}>{description}</Typography>
        </Box>
    </Box>
);

// --- Données des modules ---
const modules = [
    {
        id: 'etat',
        title: 'Module État',
        icon: <Assessment />,
        color: '#1565c0',
        resume: 'Visualisation de tous les journaux, statistiques et documents de synthèse de l\'activité.',
        steps: [
            { title: 'Tableaux de bord', desc: 'Accédez à la page État → Tableaux de bord pour obtenir une vue synthétique de l\'activité en temps réel (revenus, dossiers en cours, alertes).' },
            { title: 'Statistiques', desc: 'Consultez les statistiques périodiques (semaine, mois, année) par type de produit ou par client.' },
            { title: 'Relevé de compte client', desc: 'Sélectionnez un client dans la liste pour afficher l\'historique de ses achats, paiements et solde courant.' },
            { title: 'Journaux', desc: 'Accédez aux journaux : de ventes (toutes les factures), de caisse (encaissements physiques), de paiements (règlements) et de facturation (documents émis).' },
            { title: 'Liste clients', desc: 'Retrouvez rapidement un client, consultez ses données de contact et son historique.' },
            { title: 'Journal des événements', desc: 'Suivi chronologique de toutes les actions effectuées dans le logiciel (qui a fait quoi et quand).' },
        ],
        tips: ['Les journaux sont exportables en PDF ou Excel depuis leur interface.', 'Les tableaux de bord sont personnalisables selon le profil de l\'utilisateur.']
    },
    {
        id: 'facturation',
        title: 'Module Facturation',
        icon: <Receipt />,
        color: '#2e7d32',
        resume: 'Gestion centralisée de toutes les factures, paiements et documents financiers, y compris les billets d\'avion.',
        steps: [
            { title: 'Liste des factures', desc: 'Accédez à Facturation → Liste Factures. Vous verrez toutes les factures émises, quels que soient le produit (circuit, séjour, billet...). Utilisez les filtres pour rechercher par client, date ou montant.' },
            { title: 'Créer une facture', desc: 'Cliquez sur "Nouvelle Facture" et remplissez les informations du client, le(s) produit(s) et le montant. Le numéro de facture est généré automatiquement de façon séquentielle.' },
            { title: 'Documents', desc: 'Consultez tous les documents financiers attachés : devis, bons de commande, confirmations de réservation.' },
            { title: 'Suivi des Paiements', desc: 'Enregistrez les paiements reçus (virement, espèces, chèque) et suivez les soldes restants. Les factures sont marquées automatiquement comme "soldées" une fois payées intégralement.' },
            { title: 'Gestion Billets', desc: 'Gérez le portefeuille de billets d\'avion : consultez, émettez ou annulez des billets liés à vos dossiers.' },
            { title: 'Journal Vente Billets', desc: 'Consultez le journal spécifique à la vente de billets pour un suivi comptable précis.' },
        ],
        tips: ['la numérotation des factures est unique et continue pour garantir la conformité comptable.', 'Un paiement partiel génère automatiquement un solde à payer visible dans "Suivi Paiements".']
    },
    {
        id: 'utilitaires',
        title: 'Module Utilitaires',
        icon: <Settings />,
        color: '#6a1b9a',
        resume: 'Paramétrage de l\'application, gestion des ressources et outils avancés. Réservé aux profils Administrateur et Production.',
        steps: [
            { title: 'Paramètres société', desc: 'Renseignez les informations de votre agence (nom, adresse, logo, coordonnées). Ces données apparaissent sur vos factures et documents officiels.' },
            { title: 'Utilisateurs', desc: 'Créez et gérez les comptes utilisateurs. Attribuez des profils (Administrateur, Production, Commercial) pour définir les droits d\'accès.' },
            { title: 'Gestion des types d\'activité', desc: 'Configurez les types de prestations proposées (circuit, séjour, excursion, transfert, vol). Chaque type peut avoir ses propres champs personnalisés.' },
            { title: 'Plan Tiers', desc: 'Gérez votre annuaire : clients, fournisseurs, partenaires. Chaque tiers a une fiche avec ses coordonnées, son type et son historique de transactions.' },
            { title: 'Produits (Circuits, Séjours, etc.)', desc: 'Créez et gérez les produits proposés : Circuits touristiques, Séjours balnéaires, Excursions à la journée et Transferts aéroport.' },
            { title: 'Interface SAGE', desc: 'Exportez vos données comptables vers SAGE. Configurez le plan comptable et lancez les exports périodiques.' },
            { title: 'Chauffeurs & Véhicules', desc: 'Gérez votre flotte de véhicules et le registre de vos chauffeurs pour l\'affectation aux transferts.' },
        ],
        tips: ['Les modifications des Paramètres Société sont prises en compte immédiatement sur tous les nouveaux documents.', 'La suppression d\'un utilisateur est irréversible. Préférez la désactivation du compte.']
    },
    {
        id: 'outils',
        title: 'Outils Rapides',
        icon: <Calculate />,
        color: '#e65100',
        resume: 'Accès rapide aux outils annexes : Cotations, Calendrier et Calculatrice de marges.',
        steps: [
            { title: 'Cotations', desc: 'Créez des simulations de prix pour un groupe. Renseignez les charges individuelles (hébergement par PAX) et les charges collectives (transport). L\'outil calcule automatiquement le prix de vente par PAX selon la taille du groupe et les marges (15% ou 20%).' },
            { title: 'Ajouter une ligne dans les cotations', desc: 'Cliquez sur "+ Ajouter une ligne" pour ajouter un hébergement ou un frais. Remplissez les champs puis les totaux se mettent à jour automatiquement.' },
            { title: 'Sauvegarder une cotation', desc: 'Cliquez sur le bouton "Enregistrer" (icône disquette en haut), donnez un nom à votre cotation. Elle sera disponible dans la liste "Cotations sauvegardées" pour être réutilisée.' },
            { title: 'Calendrier', desc: 'Visualisez tous les transferts et départs planifiés sur un calendrier mensuel. Cliquez sur un événement pour voir les détails.' },
        ],
        tips: ['Le prix de vente "15%" signifie que la marge de 15% est incluse dans ce prix (= prix_coûtant / 0.85).', 'Vous pouvez exporter une cotation en PDF via le bouton PDF en haut de page.']
    }
];

const DocumentationPage = () => {
    const [activeModule, setActiveModule] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const navigate = useNavigate();

    return (
        <Box sx={{ padding: 3, maxWidth: 1100, margin: '0 auto' }}>

            {/* En-tête */}
            <Paper sx={{ padding: 3, mb: 3, background: `linear-gradient(135deg, ${COLORS.secondary}22 0%, white 100%)`, borderLeft: `5px solid ${COLORS.secondary}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <MenuBook sx={{ fontSize: 44, color: COLORS.secondary }} />
                    <Box>
                        <Typography variant="h4" sx={{ color: COLORS.secondary, fontWeight: 'bold', lineHeight: 1.2 }}>
                            Guide d'utilisation
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: '#666' }}>
                            TravelManager AFT — Didacticiel rapide
                        </Typography>
                    </Box>
                </Box>
                <Alert severity="info" icon={<Info />} sx={{ mt: 2 }}>
                    Ce guide vous explique pas à pas comment utiliser chaque module de l'application. Cliquez sur un module ci-dessous pour dérouler son didacticiel.
                </Alert>
            </Paper>

            {/* Navigation rapide */}
            <Paper sx={{ padding: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1.5, textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.75rem' }}>
                    Navigation rapide
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {modules.map(m => (
                        <Chip
                            key={m.id}
                            icon={m.icon}
                            label={m.title}
                            clickable
                            onClick={() => setActiveModule(activeModule === m.id ? null : m.id)}
                            sx={{
                                backgroundColor: activeModule === m.id ? m.color : 'transparent',
                                color: activeModule === m.id ? 'white' : m.color,
                                border: `1px solid ${m.color}`,
                                '& .MuiChip-icon': { color: activeModule === m.id ? 'white' : m.color }
                            }}
                        />
                    ))}
                </Box>
            </Paper>

            {/* Modules */}
            {modules.map((mod) => (
                <Accordion
                    key={mod.id}
                    expanded={activeModule === mod.id}
                    onChange={() => setActiveModule(activeModule === mod.id ? null : mod.id)}
                    sx={{ mb: 1.5, borderLeft: `4px solid ${mod.color}`, '&:before': { display: 'none' } }}
                >
                    <AccordionSummary expandIcon={<ExpandMore />} sx={{ backgroundColor: `${mod.color}11` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ color: mod.color }}>{mod.icon}</Box>
                            <Box>
                                <Typography variant="h6" sx={{ color: mod.color, fontWeight: 'bold', lineHeight: 1.2 }}>
                                    {mod.title}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                    {mod.resume}
                                </Typography>
                            </Box>
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 2.5, pb: 2, px: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
                            📋 Comment utiliser ce module ?
                        </Typography>

                        {mod.steps.map((step, i) => (
                            <Step key={i} number={i + 1} title={step.title} desc={step.desc} />
                        ))}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#444', mb: 1 }}>
                            💡 Conseils & Astuces
                        </Typography>
                        {mod.tips.map((tip, i) => (
                            <Alert key={i} severity="success" icon={<CheckCircleOutline />} sx={{ mb: 1, fontSize: '0.82rem' }}>
                                {tip}
                            </Alert>
                        ))}
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Aide Support */}
            <Paper sx={{ padding: 2.5, mt: 3, backgroundColor: '#f9f9f9', borderTop: `3px solid ${COLORS.secondary}` }}>
                <Typography variant="h6" sx={{ color: COLORS.secondary, mb: 1 }}>
                    🛠️ Aide & Support technique
                </Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>
                    Pour toute question ou anomalie, contactez l'équipe de développement en précisant : <strong>le module concerné</strong>, <strong>l'action effectuée</strong> et <strong>le message d'erreur éventuellement affiché</strong>.
                </Typography>
            </Paper>
        </Box>
    );
};

export default DocumentationPage;
